import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  favoritesApi,
  type FavoriteEntityType,
  type FavoriteRow,
} from "@/services/api";
import { useAuth } from "./auth";
import { useI18n } from "./i18n";

type FavoritesContextValue = {
  items: FavoriteRow[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  isSaved: (entityType: FavoriteEntityType | string, entityId: string) => boolean;
  toggle: (entityType: FavoriteEntityType | string, entityId: string) => Promise<void>;
  busyKey: string | null;
};

const FavoritesContext = createContext<FavoritesContextValue | null>(null);

function keyOf(entityType: string, entityId: string) {
  return `${entityType}:${entityId}`;
}

export function FavoritesProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { locale } = useI18n();
  const [items, setItems] = useState<FavoriteRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!user) {
      setItems([]);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await favoritesApi.list(locale);
      setItems(data.items || []);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load favorites");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [user, locale]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const isSaved = useCallback(
    (entityType: FavoriteEntityType | string, entityId: string) =>
      items.some((f) => f.entity_type === entityType && f.entity_id === entityId),
    [items],
  );

  const toggle = useCallback(
    async (entityType: FavoriteEntityType | string, entityId: string) => {
      if (!user) {
        throw new Error("LOGIN_REQUIRED");
      }
      const k = keyOf(entityType, entityId);
      setBusyKey(k);
      setError(null);
      try {
        if (isSaved(entityType, entityId)) {
          await favoritesApi.removeByEntity(entityType, entityId);
          setItems((prev) =>
            prev.filter((f) => !(f.entity_type === entityType && f.entity_id === entityId)),
          );
        } else {
          const res = await favoritesApi.add(entityType, entityId);
          setItems((prev) => {
            if (prev.some((f) => f.entity_type === entityType && f.entity_id === entityId)) {
              return prev;
            }
            return [
              {
                id: res.id,
                entity_type: entityType,
                entity_id: entityId,
              },
              ...prev,
            ];
          });
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : "Save failed");
        throw e;
      } finally {
        setBusyKey(null);
      }
    },
    [user, isSaved],
  );

  const value = useMemo(
    () => ({ items, loading, error, refresh, isSaved, toggle, busyKey }),
    [items, loading, error, refresh, isSaved, toggle, busyKey],
  );

  return <FavoritesContext.Provider value={value}>{children}</FavoritesContext.Provider>;
}

export function useFavorites() {
  const ctx = useContext(FavoritesContext);
  if (!ctx) throw new Error("useFavorites must be used within FavoritesProvider");
  return ctx;
}
